<script lang="ts" setup>
import { ref, computed, provide } from 'vue'
import type { PropType, VNodeRef } from 'vue'
import BsProductSharingPostList from '@/legacy/components/ProductSharingPostList.vue'
import BsProductSharingPostCard from '@/legacy/components/ProductSharingPostCard.vue'
import BsDialogAction from '@/legacy/components/DialogAction.vue'
import BsSnackBar from '@/legacy/components/SnackBar.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import {
	checkProductSharingEnable,
	productSharingPostPerPage,
	getProductSharingUrl,
} from '@/legacy/ts/productSharing'
import usePostCard from '@/legacy/ts/usePostCard'
import useSnackBar from '@/legacy/ts/useSnackBar'
import { useBreakpoints, getCurrentGaPageName } from '@/lib/utils'
import { createGAEvent, GaGeneralEvent } from '@/assets/ts/customEvent'

const props = defineProps({
	postCardTextInfo: {
		type: Object as PropType<any>,
		required: true,
	},
	postListText: {
		type: Object as PropType<any>,
		required: true,
	},
	sideText: {
		type: Object as PropType<any>,
		required: true,
	},
	localCurrencyInfo: {
		type: Array,
		default: null,
	},
})
const icons = [
	'icon-expresscoRating',
	'icon-buyaholicSmartBuyer',
	'icon-expresscoPopularItems',
]
const { isShow, messages, dismiss, addMessage } = useSnackBar()
const { isLarge: isDesktop } = useBreakpoints()
const postListRef = ref<VNodeRef | null>(null)
const isProductSharingEnable = ref(checkProductSharingEnable())
const columnCount = computed(() => (isDesktop.value ? 3 : 2))

const {
	postDetailCurrent,
	categoryMap,
	warehouseIdMap,
	interestsMap,
	supportedCurrencies,
	buyforyouSupportWarehouse,
	priceLocal,
	priceLocalPost,
	DialogType,
	isShowDialogPostDetail,
	isLoadingPostDetail,
	hideDialogPostDetail,
	setPostActions,
} = usePostCard(postListRef)

const goProductSharing = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: getCurrentGaPageName(),
		button_name: 'View Sharing - Community',
	})
	const HOME_ROUTE_COUNTRIES = ['hk', 'mo', 'tw']
	const routeType = HOME_ROUTE_COUNTRIES.includes(
		window.BUYANDSHIP.settings.current_country,
	)
		? 'home'
		: 'recommended'
	window.location.href = getProductSharingUrl(routeType)
}

const goIntroPage = (link: string) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: getCurrentGaPageName(),
		button_name: 'Learn more - Community',
	})
	window.location.href = link
}

provide('addMessage', addMessage)
</script>

<template>
	<section v-if="isProductSharingEnable">
		<div class="product-sharing-container relative lg:justify-between">
			<div
				class="col-span-12 mb-16px mdp:sticky mdp:top-[120px] mdp:col-span-4 mdp:mb-0 mdp:self-start"
			>
				<h2 class="typography-h2-str text-center text-n-950 mdp:text-left">
					{{ props?.sideText.title }}
				</h2>
				<div
					class="typography-s1-reg pt-close-friends text-center text-n-500 mdp:text-left"
				>
					{{ props?.sideText.subTitle }}
				</div>
				<div class="flex flex-wrap gap-close-friends py-good-friends">
					<BaseButton
						variant="primary"
						size="medium"
						class="w-full mdp:w-max"
						@click="goProductSharing"
					>
						{{ props?.sideText.buttonPrimary }}
					</BaseButton>
					<BaseButton
						variant="secondary"
						size="medium"
						class="w-full mdp:w-max"
						@click="goIntroPage(props?.sideText.linkSecondary)"
					>
						{{ props?.sideText.buttonSecondary }}
					</BaseButton>
				</div>
				<div class="flex flex-col gap-close-friends">
					<div
						v-for="(item, index) in props?.sideText.advantages"
						:key="item"
						class="flex items-center"
					>
						<div
							class="icon mr-close-friends flex h-[32px] w-[32px] items-center justify-center [&>svg]:h-[32px] [&>svg]:w-[32px]"
						>
							<i class="bs-new-icon h-32px w-32px" :class="icons[index]"></i>
						</div>
						<div class="typography-s1-emp text-n-950">{{ item }}</div>
					</div>
				</div>
			</div>
			<div class="col-span-12 mdp:col-span-8 lg:col-span-7 lg:col-start-6">
				<BsProductSharingPostList
					ref="postListRef"
					@post-item-like="setPostActions"
					:column-count="columnCount"
					:post-per-page="productSharingPostPerPage.explore"
					:text="postListText"
					:local-currency-info="localCurrencyInfo"
					is-index-login
				></BsProductSharingPostList>
			</div>
		</div>

		<BsDialogAction
			v-model="isShowDialogPostDetail"
			:type="DialogType.BottomSheet"
			:close-text="postCardTextInfo.dialog.close"
			:is-hide-button-close="false"
			@on-hide="hideDialogPostDetail('dialog')"
		>
			<template #content>
				<BsProductSharingPostCard
					@update-post="setPostActions"
					@back="hideDialogPostDetail('back')"
					:text="postCardTextInfo"
					:detail="postDetailCurrent"
					:interest="interestsMap"
					:categories="categoryMap"
					:warehouses="warehouseIdMap"
					:currencies="supportedCurrencies"
					:support-warehouses="buyforyouSupportWarehouse"
					:price-local="priceLocal"
					:price-local-post="priceLocalPost"
					:is-type-modal="true"
					:is-loading="isLoadingPostDetail"
					:local-currency-info="localCurrencyInfo"
					:post-list-text-info="postListText"
				></BsProductSharingPostCard>
			</template>
		</BsDialogAction>

		<Teleport to="body">
			<BsSnackBar
				:content="messages[0]"
				:is-show="isShow"
				@dismiss="dismiss"
			></BsSnackBar>
		</Teleport>
	</section>
</template>
